import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Megaphone, Calendar, User } from "lucide-react";
import { api } from "../../utils/api";
import { Announcement } from "../../types";
import { toSydneyDate } from "../../utils/helpers";

export default function AnnouncementPage() {
  const { id } = useParams<{ id: string }>();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      setIsLoading(true);
      try {
        const response = await api.get("/api/dashboard");
        if (response.data) {
          const announcements: Announcement[] = response.data.announcements || [];
          const found = announcements.find((a) => a.id === id);
          if (found) {
            setAnnouncement(found);
          }
        }
      } catch (error) {
        console.error("Failed to fetch announcement:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnnouncement();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!announcement) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Announcement Not Found</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              The announcement you are looking for does not exist or has been removed.
            </p>
            <Link to="/dashboard" className="mt-4 inline-block">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard">
          <Button variant="outline" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">News & Announcements</h1>
      </div>

      {/* Announcement Card */}
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Megaphone className="h-4 w-4" />
            <span className="text-sm font-medium">Announcement</span>
            <span className="text-sm">·</span>
            <span className="text-sm flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {toSydneyDate(announcement.createdAt)}
            </span>
            {announcement.createdByName && (
              <>
                <span className="text-sm">·</span>
                <span className="text-sm flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {announcement.createdByName}
                </span>
              </>
            )}
          </div>
          <CardTitle className="text-2xl sm:text-3xl leading-tight">
            {announcement.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {announcement.imageUrl && (
            <img
              src={announcement.imageUrl}
              alt={announcement.title}
              className="w-full rounded-lg object-cover max-h-[400px]"
            />
          )}
          <div
            className="prose prose-slate max-w-none dark:prose-invert ql-editor p-0 overflow-visible"
            dangerouslySetInnerHTML={{ __html: announcement.content }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
